import { useEffect, useMemo, useState } from 'react'
import { isEqual, camelCase, startCase } from 'lodash'
import { useAuth0 } from '@auth0/auth0-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { AppStateContext } from '../../../providers/AppStateContext'
import { generatePermalink } from '../../../utils/formatting'
import { didOsmRelationsChange, didWaysChange, nonBlankOsmRelations, nonBlankWays } from '../../../utils/osmIds'
import { IntRange } from '../../../utils/typing'

import { Trail } from '../../../models/Trail.model'
import { TrailAddEvent } from '../../../events/TrailAddEvent'
import { TrailUpdateEvent } from '../../../events/TrailUpdateEvent'
import { AppPermissions } from '../../../models/AppPermissions.model'
import { USStates } from '../../../models/USStates.model'
import { TrailSystem } from '../../../models/TrailSystem.model'
import { TrailRelations } from '../../../models/TrailRelations.model'
import { TrailSystemRelations } from '../../../models/TrailSystemRelations.model'
import { Difficulties } from '../../../models/Difficulties.model'

import UserService from '../../../services/UserService/UserService'
import { WeatherGroupService } from '../../../services/WeatherGroupService/WeatherGroupService'
import { TrailSystemService } from '../../../services/TrailSystemService/TrailSystemService'
import { TrailService } from '../../../services/TrailService/TrailService'
import { TuningProfileService } from '../../../services/TuningProfileService/TuningProfileService'

import {
  Alert,
  Stack,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Button,
  Select,
  MenuItem,
  InputAdornment,
  Typography,
  Divider,
  Box,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'

import Loading from '../../../components/Loading/Loading.component'
import DetailInput from '../../../components/DetailInput/DetailInput.component'
import DetailSelectWithCreateNew from '../../../components/DetailSelectWithCreateNew/DetailSelectWithCreateNew.component'
import { useAppNavigate } from '../../../utils/url'
import { ConditionalStatusGroupService } from '../../../services/ConditionalStatusGroupService/ConditionalStatusGroupService'
import GoogleMapsFetchDialog from '../../../components/GoogleMapsFetchDialog/GoogleMapsFetchDialog.component'
import { TrailWay } from '../../../models/TrailWay.model'
import { TrailRelation } from '../../../models/TrailRelation.model'
import SectionedForm, { FormSection } from '../../../components/Sections/SectionedForm.component'
import SectionSubheading from '../../../components/Sections/SectionSubheading.component'
import WeatherGroupPriorityList from '../../../components/WeatherGroupPriorityList/WeatherGroupPriorityList.component'
import { TrailSectionIds, trailSections } from './TrailSections'
import { compareByName, sortResponseByName } from '../../../utils/sorting'

export type TrailFormProps = {
  trail?: Trail
  trailSystem?: TrailSystem
  onAdd?: (trail: Trail) => void
  onEdit?: (trail: Trail) => void
  onCancel: () => void
  // used to remotely trigger the save action
  saveTrigger?: number
  disableRedirect?: boolean
  // section of the form to scroll to when it opens (see TrailSectionIds)
  initialSection?: string
}

const TrailForm = (props: TrailFormProps) => {
  const { trail, trailSystem, onCancel, onAdd, onEdit, saveTrigger, disableRedirect = false, initialSection } = props

  const navigate = useAppNavigate()

  const [isLoading, setIsLoading] = useState(true)

  const { user } = useAuth0()
  const hasViewWeatherGroupPermissions = UserService.hasPermissions(user, [AppPermissions.viewWeatherGroup])
  const hasViewConditionalStatusGroupPermissions = UserService.hasPermissions(user, [
    AppPermissions.viewConditionalStatus,
  ])

  const isAdding = typeof onAdd === 'function'
  const isEditing = typeof onEdit === 'function'

  const [tempTrail, setTempTrail] = useState<
    Partial<Trail> & {
      weatherGroupsWithPriority?: { weatherGroupId: number; priority: number }[] | null
      displayWeatherGroupId?: number | null
      conditionalStatusGroupIds?: number[]
    }
  >({
    ...trail,
    weatherGroupsWithPriority: trail?.weatherGroups
      ? trail.weatherGroups.map((wg) => ({
          weatherGroupId: wg.id,
          priority: wg.priority,
        }))
      : [],
    displayWeatherGroupId: trail?.displayWeatherGroup?.id,
    conditionalStatusGroupIds: trail?.conditionalStatusGroups?.map((each) => each.id),
    ways: trail?.ways
      ? trail.ways.map((way) => ({
          wayId: way.wayId,
        }))
      : [],
    osmRelations: trail?.osmRelations
      ? trail.osmRelations.map((relation) => ({
          relationId: relation.relationId,
        }))
      : [],
    trailforksIds: trail?.trailforksIds
      ? trail.trailforksIds.map((link) => ({ trailforksTrailId: link.trailforksTrailId }))
      : [],
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGoogleMapsDialogOpen, setIsGoogleMapsDialogOpen] = useState(false)
  const [isParkingDialogOpen, setIsParkingDialogOpen] = useState(false)

  const queryClient = useQueryClient()

  const weatherGroupService = useMemo(() => new WeatherGroupService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isLoadingWeatherGroups, data: weatherGroupsResponse } = useQuery(
    ['WeatherGroupService.search', { pageSize: 0 }, AppStateContext.getRegion()],
    () => weatherGroupService.search({ pageSize: 0 }),
    {
      enabled: hasViewWeatherGroupPermissions, // prevent the query from running unless they have the permissions
      // list options alphabetically
      select: sortResponseByName,
      onError: (err) => {
        SnackbarContext.show(
          `Failed to fetch weather groups for the form: ${typeof err === 'string' ? err : ''}`,
          'error',
        )
        console.error(err)
      },
    },
  )

  const conditionalStatusGroupService = useMemo(() => new ConditionalStatusGroupService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isLoadingConditionalStatusGroups, data: conditionalStatusGroupsResponse } = useQuery(
    ['ConditionalStatusGroupService.search', { pageSize: 0 }, AppStateContext.getRegion()],
    () => conditionalStatusGroupService.search({ pageSize: 0 }),
    {
      // list options alphabetically
      select: sortResponseByName,
      enabled: hasViewConditionalStatusGroupPermissions, // prevent the query from running unless they have the permissions
      onError: (err) => {
        SnackbarContext.show(
          `Failed to fetch conditional status groups for the form: ${typeof err === 'string' ? err : ''}`,
          'error',
        )
        console.error(err)
      },
    },
  )

  const [tempTrailSystem, setTempTrailSystem] = useState<TrailSystem | undefined>(trailSystem)
  const [isLoadingTrailSystems, setIsLoadingTrailSystems] = useState(true)
  const trailSystemService = useMemo(() => new TrailSystemService(), []) // memo to prevent re-creating service on every render
  const { data: trailSystemsResponse } = useQuery(
    ['TrailSystemService.search', { pageSize: 0 }, AppStateContext.getRegion()],
    () => trailSystemService.search({ pageSize: 0 }),
    {
      enabled: !trail && !trailSystem, // only query if we are adding a trail and no trail system was provided
      onError: (err) => {
        SnackbarContext.show(
          `Failed to fetch trail systems for the form: ${typeof err === 'string' ? err : ''}`,
          'error',
        )
        console.error(err)
      },
    },
  )
  // useEffect usage instead of onSuccess or onSettled to ensure we always trigger even when the query is cached
  useEffect(() => {
    if (trail || trailSystem || trailSystemsResponse) {
      setIsLoadingTrailSystems(false)
    }
  }, [trail, trailSystem, trailSystemsResponse])

  const trailService = useMemo(() => new TrailService(), []) // memo to prevent re-creating service on every render
  // Always request trailforksIds on the form's fetch (in addition to the fields the
  // search grid deliberately omits) — without it, tempTrail.trailforksIds starts empty,
  // the didChange diff below sees a phantom change, and saving after adding one new id
  // would silently delete every pre-existing mapping via the API's update reconcile.
  const formRelations = [
    TrailRelations.system,
    TrailRelations.weatherGroups,
    TrailRelations.conditionalStatusGroups,
    TrailRelations.trailforksIds,
  ]
  const { isLoading: isLoadingTrailWithRelations, data: trailWithRelations } = useQuery(
    ['TrailService.get', { id: trail?.id, relations: formRelations }, AppStateContext.getRegion()],
    () => trailService.get({ id: trail?.id, relations: formRelations }),
    {
      // Always fetch when editing so we can pick up trailforksIds and current relations.
      enabled: !!trail?.id,
      onError: (err) => {
        SnackbarContext.show(
          `Failed to fetch relation records from trail for the form: ${typeof err === 'string' ? err : ''}`,
          'error',
        )
        console.error(err)
      },
    },
  )
  // useEffect usage instead of onSuccess or onSettled to ensure we always trigger even when the query is cached
  useEffect(() => {
    if (!trailSystem) {
      setTempTrailSystem(trailWithRelations?.system)
      setIsLoadingTrailSystems(false)
    }
    if (trailWithRelations) {
      setTempTrail((prev) => ({
        ...prev,
        weatherGroupsWithPriority: (trailWithRelations.weatherGroups ?? []).map((each) => ({
          weatherGroupId: each.id,
          priority: each.priority,
        })),
        displayWeatherGroupId: trailWithRelations.displayWeatherGroup?.id,
        conditionalStatusGroupIds: trailWithRelations.conditionalStatusGroups?.map((each) => each.id),
        // Hydrate mappings from the canonical fetched record so subsequent didChange
        // diffs compare against the real DB state, not against the empty search-grid row.
        trailforksIds: (trailWithRelations.trailforksIds ?? []).map((link) => ({
          trailforksTrailId: link.trailforksTrailId,
        })),
      }))
    }
  }, [trailWithRelations])

  useEffect(() => {
    // if the trailSystem is not provided, try to look it up
    if (!trailSystem && trail && trail.system) {
      // if the trail is provided and has the system prop, no need to look up
      setTempTrailSystem(trail.system)
      setIsLoadingTrailSystems(false)
    }
  }, [trailSystem, trail])

  // fetch the parking lots that belong to this trail's system, so the admin can pick one
  // instead of re-entering coordinates. Only queries when we know the system id.
  const systemIdForParkingLots = tempTrailSystem?.id
  const { data: trailSystemWithParkingLots, isLoading: isLoadingSystemParkingLots } = useQuery(
    [
      'TrailSystemService.get',
      { id: systemIdForParkingLots, relations: [TrailSystemRelations.parkingLots] },
      AppStateContext.getRegion(),
    ],
    () =>
      trailSystemService.get({
        id: systemIdForParkingLots,
        relations: [TrailSystemRelations.parkingLots],
      }),
    {
      enabled: !!systemIdForParkingLots,
      onError: (err) => {
        SnackbarContext.show(
          `Failed to fetch parking lots for this trail system: ${typeof err === 'string' ? err : ''}`,
          'error',
        )
        console.error(err)
      },
    },
  )
  const systemParkingLots = trailSystemWithParkingLots?.parkingLots ?? []

  // when the trail's system has an active tuning profile, saving the trail's tuning values also saves them into it
  const activeTuningProfileId = trailSystemWithParkingLots?.activeTuningProfileId
  const { data: systemTuningProfiles } = useQuery(
    ['TuningProfileService.list', { trailSystemId: systemIdForParkingLots }, AppStateContext.getRegion()],
    () => new TuningProfileService().list(Number(systemIdForParkingLots)),
    { enabled: !!systemIdForParkingLots && !!activeTuningProfileId },
  )
  const activeTuningProfile = systemTuningProfiles?.find((profile) => profile.id === activeTuningProfileId)

  // fetch surface type options for the select
  const { isLoading: isLoadingSurfaceTypes, data: surfaceTypes } = useQuery(
    ['TrailService.listSurfaceTypes', AppStateContext.getRegion()],
    () => trailService.listSurfaceTypes(),
    {
      onError: (err) => {
        SnackbarContext.show(
          `Failed to fetch surface types for the form: ${typeof err === 'string' ? err : ''}`,
          'error',
        )
        console.error(err)
      },
    },
  )

  // consolidate the various loading states into one
  useEffect(() => {
    const shouldBeLoadingTrailWithRelations = !!(trail?.id && (!trailSystem || !trail.weatherGroups?.length))
    setIsLoading(true)
    if (
      ((shouldBeLoadingTrailWithRelations && !isLoadingTrailWithRelations) || !shouldBeLoadingTrailWithRelations) &&
      ((hasViewWeatherGroupPermissions && !isLoadingWeatherGroups) || !hasViewWeatherGroupPermissions) &&
      ((hasViewConditionalStatusGroupPermissions && !isLoadingConditionalStatusGroups) ||
        !hasViewConditionalStatusGroupPermissions) &&
      !isLoadingSurfaceTypes &&
      !isLoadingTrailSystems
    ) {
      setIsLoading(false)
    }
  }, [
    trail,
    trailSystem,
    isLoadingTrailWithRelations,
    hasViewWeatherGroupPermissions,
    isLoadingWeatherGroups,
    isLoadingConditionalStatusGroups,
    isLoadingSurfaceTypes,
    isLoadingTrailSystems,
  ])

  const addTrail = useMutation(
    (mutationParams: { trail: TrailAddEvent }) => {
      setIsSubmitting(true)
      return trailService.add(mutationParams.trail)
    },
    {
      onSuccess: (result) => {
        SnackbarContext.show('Trail added successfully!', 'success', APP_UPDATE_NOTICE)

        if (!disableRedirect) {
          // redirect to the new trail system
          navigate(`/trails/${result.id}`)
          // remove the search query to prevent auto refresh of the search results in case we are on that page and redirecting
          queryClient.removeQueries(['TrailService.search'])
        }
        // invalidate all other queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSubmitting(false)

        if (typeof onAdd === 'function') {
          onAdd(result)
        }
      },
      onError: (err) => {
        SnackbarContext.show(`Trail failed to add: ${err}`, 'error')
        console.error(err)
        setIsSubmitting(false)
      },
    },
  )

  const updateTrail = useMutation(
    (mutationParams: { id: number; changedProperties: TrailUpdateEvent }) => {
      setIsSubmitting(true)
      return trailService.update(mutationParams.id, mutationParams.changedProperties)
    },
    {
      onSuccess: (result) => {
        SnackbarContext.show('Trail updated successfully!', 'success', APP_UPDATE_NOTICE)

        // invalidate all queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSubmitting(false)

        if (typeof onEdit === 'function') {
          onEdit(result)
        }
      },
      onError: (err) => {
        SnackbarContext.show(`Trail failed to update: ${err}`, 'error')
        console.error(err)
        setIsSubmitting(false)
      },
    },
  )

  // Weather group handlers (multi)
  const handleWeatherGroupChange = (idx: number, field: 'weatherGroupId' | 'priority', value: number) => {
    setTempTrail((prev) => {
      const arr = prev.weatherGroupsWithPriority ? [...prev.weatherGroupsWithPriority] : []
      arr[idx] = { ...arr[idx], [field]: value }
      return { ...prev, weatherGroupsWithPriority: arr }
    })
  }

  const handleAddWeatherGroup = () => {
    const existingIds = tempTrail.weatherGroupsWithPriority?.map((w) => w.weatherGroupId) ?? []
    const available = weatherGroupsResponse?.data.find((w) => !existingIds.includes(w.id))

    if (!available) return // No more unique weather groups to add

    setTempTrail((prev) => ({
      ...prev,
      weatherGroupsWithPriority: [
        ...(prev.weatherGroupsWithPriority ?? []),
        { weatherGroupId: available.id, priority: (prev.weatherGroupsWithPriority?.length ?? 0) + 1 },
      ],
    }))
  }

  const handleRemoveWeatherGroup = (idx: number) => {
    setTempTrail((prev) => {
      const arr = prev.weatherGroupsWithPriority ? [...prev.weatherGroupsWithPriority] : []
      arr.splice(idx, 1)
      return { ...prev, weatherGroupsWithPriority: arr }
    })
  }

  const handleReorderWeatherGroups = (fromIndex: number, toIndex: number) => {
    const updated = [...(tempTrail.weatherGroupsWithPriority ?? [])]
    const [movedItem] = updated.splice(fromIndex, 1)
    updated.splice(toIndex, 0, movedItem)

    const updatedWithPriority = updated.map((item, i) => ({
      ...item,
      priority: i + 1,
    }))

    setTempTrail((prev) => ({
      ...prev,
      weatherGroupsWithPriority: updatedWithPriority,
    }))
  }

  const handleSubmit = () => {
    if (!tempTrail.name) {
      SnackbarContext.show(`Trail name is required`, 'error')
      return
    }
    if (!tempTrail.street) {
      SnackbarContext.show(`Trail street is required`, 'error')
      return
    }
    if (!tempTrail.city) {
      SnackbarContext.show(`Trail city is required`, 'error')
      return
    }
    if (!tempTrail.state) {
      SnackbarContext.show(`Trail state is required`, 'error')
      return
    }
    if (!tempTrail.weatherResistance) {
      SnackbarContext.show(`Trail weather resistance is required`, 'error')
      return
    }

    if (!trail) {
      if (!tempTrailSystem?.id) {
        SnackbarContext.show(`Trail system is required`, 'error')
        return
      }
      // format the data to match the API
      const formattedTrail: TrailAddEvent = {
        name: tempTrail.name,
        systemId: tempTrailSystem.id,
        weatherGroupsWithPriority: tempTrail.weatherGroupsWithPriority || undefined,
        displayWeatherGroupId: tempTrail.displayWeatherGroupId || undefined,
        conditionalStatusGroupIds: tempTrail.conditionalStatusGroupIds || undefined,
        difficultyId: tempTrail.difficultyId || undefined,
        description: tempTrail.description || undefined,
        featuredImgUrl: tempTrail.featuredImgUrl || undefined,
        surfaceType: tempTrail.surfaceType || undefined,
        distance: tempTrail.distance || undefined,
        descent: tempTrail.descent || undefined,
        climb: tempTrail.climb || undefined,
        gpsLat: tempTrail.gpsLat || undefined,
        gpsLong: tempTrail.gpsLong || undefined,
        parkingGpsLat: tempTrail.parkingGpsLat || undefined,
        parkingGpsLong: tempTrail.parkingGpsLong || undefined,
        street: tempTrail.street,
        city: tempTrail.city,
        state: tempTrail.state,
        weatherResistance: tempTrail.weatherResistance,
        saturationModifier: tempTrail.saturationModifier || undefined,
        dryTimeModifier: String(tempTrail.dryTimeModifier ?? '') === '' ? undefined : tempTrail.dryTimeModifier,
        sortWeight: tempTrail.sortWeight || undefined,
        permalink: tempTrail.permalink || undefined,
        active: tempTrail.active,
        precipitationLookbackDays: tempTrail.precipitationLookbackDays ?? undefined,
        freezeThawEnabled: tempTrail.freezeThawEnabled ?? undefined,
        ways: nonBlankWays(tempTrail.ways),
        osmRelations: nonBlankOsmRelations(tempTrail.osmRelations),
        // Trailforks push: tri-state and pipe-separated repeatable TF ids.
        trailforksSyncEnabled: tempTrail.trailforksSyncEnabled ?? undefined,
        trailforksIds: (tempTrail.trailforksIds ?? []).filter(
          (link) => link.trailforksTrailId && link.trailforksTrailId.toString().trim() !== '',
        ),
      }

      // adding a new trail
      addTrail.mutate({ trail: formattedTrail })
    } else {
      // identify the properties that have changed
      const didChange = (field: keyof Trail) => {
        return tempTrail[field] !== undefined && tempTrail[field] !== trail[field]
      }

      if (!tempTrail.permalink) {
        SnackbarContext.show(`Trail permalink cannot be blank`, 'error')
        return
      }

      // Compare weatherGroupsWithPriority arrays
      const originalWeatherGroupsWithPriority = trail.weatherGroups
        ? trail.weatherGroups.map((wg) => ({
            weatherGroupId: wg.id,
            priority: wg.priority,
          }))
        : []
      const weatherGroupsChanged =
        JSON.stringify(tempTrail.weatherGroupsWithPriority ?? []) !== JSON.stringify(originalWeatherGroupsWithPriority)

      const changedProperties: TrailUpdateEvent = {
        id: trail.id,
        name: didChange('name') ? tempTrail.name : undefined,
        weatherGroupsWithPriority: weatherGroupsChanged ? tempTrail.weatherGroupsWithPriority ?? undefined : undefined,
        displayWeatherGroupId:
          tempTrail.displayWeatherGroupId !== trail.displayWeatherGroup?.id
            ? tempTrail.displayWeatherGroupId || null
            : undefined,
        conditionalStatusGroupIds: !isEqual(
          tempTrail.conditionalStatusGroupIds,
          trail.conditionalStatusGroups?.map((conditionalStatusGroup) => conditionalStatusGroup.id),
        )
          ? tempTrail.conditionalStatusGroupIds
          : undefined,
        ways: didWaysChange(tempTrail.ways, trail.ways) ? nonBlankWays(tempTrail.ways) : undefined,
        osmRelations: didOsmRelationsChange(tempTrail.osmRelations, trail.osmRelations)
          ? nonBlankOsmRelations(tempTrail.osmRelations)
          : undefined,
        difficultyId: didChange('difficultyId') ? tempTrail.difficultyId : undefined,
        description: didChange('description') ? tempTrail.description : undefined,
        featuredImgUrl: didChange('featuredImgUrl') ? tempTrail.featuredImgUrl : undefined,
        surfaceType: didChange('surfaceType') ? tempTrail.surfaceType : undefined,
        distance: didChange('distance') ? tempTrail.distance : undefined,
        descent: didChange('descent') ? tempTrail.descent : undefined,
        climb: didChange('climb') ? tempTrail.climb : undefined,
        gpsLat: didChange('gpsLat') ? tempTrail.gpsLat : undefined,
        gpsLong: didChange('gpsLong') ? tempTrail.gpsLong : undefined,
        parkingGpsLat: didChange('parkingGpsLat') ? tempTrail.parkingGpsLat : undefined,
        parkingGpsLong: didChange('parkingGpsLong') ? tempTrail.parkingGpsLong : undefined,
        parkingLotId:
          tempTrail.parkingLotId !== undefined && tempTrail.parkingLotId !== (trail.parkingLotId ?? null)
            ? tempTrail.parkingLotId
            : undefined,
        street: didChange('street') ? tempTrail.street : undefined,
        city: didChange('city') ? tempTrail.city : undefined,
        state: didChange('state') ? tempTrail.state : undefined,
        weatherResistance: didChange('weatherResistance') ? tempTrail.weatherResistance : undefined,
        saturationModifier: didChange('saturationModifier')
          ? String(tempTrail.saturationModifier) === ''
            ? null
            : tempTrail.saturationModifier
          : undefined,
        dryTimeModifier: didChange('dryTimeModifier')
          ? String(tempTrail.dryTimeModifier) === ''
            ? null
            : tempTrail.dryTimeModifier
          : undefined,
        sortWeight: didChange('sortWeight') ? tempTrail.sortWeight : undefined,
        permalink: didChange('permalink') ? tempTrail.permalink : undefined,
        active: didChange('active') ? tempTrail.active : undefined,
        precipitationLookbackDays: didChange('precipitationLookbackDays')
          ? String(tempTrail.precipitationLookbackDays) === ''
            ? null
            : tempTrail.precipitationLookbackDays
          : undefined,
        freezeThawEnabled: didChange('freezeThawEnabled') ? tempTrail.freezeThawEnabled : undefined,
        trailforksSyncEnabled: didChange('trailforksSyncEnabled') ? tempTrail.trailforksSyncEnabled : undefined,
        // Compare against trailWithRelations (the canonical fetched record) rather than
        // the search-grid `trail` prop, which does not carry trailforksIds. Comparing
        // against the empty search row would treat every hydrated set as a phantom
        // change and trigger the API's update reconcile to delete every mapping.
        trailforksIds: !isEqual(
          tempTrail.trailforksIds ?? [],
          (trailWithRelations?.trailforksIds ?? trail.trailforksIds ?? []).map((link) => ({
            trailforksTrailId: link.trailforksTrailId,
          })),
        )
          ? (tempTrail.trailforksIds ?? []).filter(
              (link) => link.trailforksTrailId && link.trailforksTrailId.toString().trim() !== '',
            )
          : undefined,
      }

      // edit an existing trail
      if (!Object.keys(changedProperties).filter((key) => key !== 'id').length) {
        // nothing changed so just close the dialog
        onCancel()
      } else {
        updateTrail.mutate({ id: trail.id, changedProperties })
      }
    }
  }

  useEffect(() => {
    if (saveTrigger) {
      // if outside consumer triggered save, then submit
      handleSubmit()
    }
  }, [saveTrigger])

  const handleCancel = () => {
    if (typeof onCancel === 'function') {
      onCancel()
    }
  }

  const keyDownSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  const transformDescentValue = (raw: unknown): string | number => {
    if (raw === '') return ''
    const num = Number(raw)
    return isNaN(num) ? '' : -Math.abs(num)
  }

  const displayDescentValue = (val: unknown): string | number => {
    if (val === undefined || val === null || val === '') return ''
    if (val === 0) return 0
    const num = Number(val)
    return isNaN(num) ? '' : Math.abs(num)
  }
  const addWayId = () => {
    setTempTrail((prev) => ({
      ...prev,
      ways: [
        ...(prev.ways ?? []),
        {
          wayId: '',
        },
      ],
    }))
  }

  const removeWayId = (index: number) => {
    setTempTrail((prev) => ({
      ...prev,
      ways: prev.ways?.filter((_, i) => i !== index) || [],
    }))
  }

  const updateWayId = <K extends keyof TrailWay>(index: number, field: K, value: TrailWay[K]) => {
    setTempTrail((prev) => ({
      ...prev,
      ways: prev.ways?.map((lot, i) => (i === index ? { ...lot, [field]: value } : lot)) || [],
    }))
  }

  const addRelationId = () => {
    setTempTrail((prev) => ({
      ...prev,
      osmRelations: [...(prev.osmRelations ?? []), { relationId: '' }],
    }))
  }

  const removeRelationId = (index: number) => {
    setTempTrail((prev) => ({
      ...prev,
      osmRelations: prev.osmRelations?.filter((_, i) => i !== index) || [],
    }))
  }

  const updateRelationId = <K extends keyof TrailRelation>(index: number, field: K, value: TrailRelation[K]) => {
    setTempTrail((prev) => ({
      ...prev,
      osmRelations:
        prev.osmRelations?.map((relation, i) => (i === index ? { ...relation, [field]: value } : relation)) || [],
    }))
  }

  // Trailforks id links: mirrors ways/osmRelations exactly.
  const addTrailforksId = () => {
    setTempTrail((prev) => ({
      ...prev,
      trailforksIds: [...(prev.trailforksIds ?? []), { trailforksTrailId: '' }],
    }))
  }

  const removeTrailforksId = (index: number) => {
    setTempTrail((prev) => ({
      ...prev,
      trailforksIds: prev.trailforksIds?.filter((_, i) => i !== index) || [],
    }))
  }

  const updateTrailforksId = (index: number, value: string) => {
    setTempTrail((prev) => ({
      ...prev,
      trailforksIds:
        prev.trailforksIds?.map((link, i) => (i === index ? { ...link, trailforksTrailId: value } : link)) || [],
    }))
  }
  return (
    <>
      {(isSubmitting || isLoading) && <Loading sx={{ py: 20 }} />}
      {!isSubmitting && !isLoading && (
        <>
          <SectionedForm sections={trailSections} initialSection={initialSection}>
            {/* --- General ------------------------------------------------------ */}
            <FormSection id={TrailSectionIds.general}>
              {isAdding && !trailSystem ? (
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="trail-system-select-label">Trail System</InputLabel>
                  <Select
                    labelId="trail-system-select-label"
                    id="trail-system-select"
                    value={tempTrailSystem ? tempTrailSystem.id : ''}
                    label="Trail System"
                    onChange={(e) => {
                      setTempTrailSystem(
                        trailSystemsResponse?.data.find((eachTrailSystem) => eachTrailSystem.id === e.target.value),
                      )
                    }}
                  >
                    <MenuItem value="">-Select-</MenuItem>
                    {/* the API defaults to sort-weight order; list the systems alphabetically */}
                    {[...(trailSystemsResponse?.data ?? [])].sort(compareByName).map((eachTrailSystem) => {
                      return (
                        <MenuItem key={eachTrailSystem.id} value={eachTrailSystem.id}>
                          {eachTrailSystem.name}
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body1" color="grey.400">
                  Trail System: <b>{tempTrailSystem?.name}</b>
                </Typography>
              )}
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Name</InputLabel>
                <OutlinedInput
                  id="trail-name"
                  label="Name"
                  value={tempTrail.name || ''}
                  required
                  onChange={(e) => {
                    setTempTrail((prev) => ({ ...prev, name: e.target.value }))
                  }}
                  onKeyDown={keyDownSubmit}
                />
              </FormControl>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Permalink</InputLabel>
                <OutlinedInput
                  id="trail-permalink"
                  label="Permalink"
                  value={tempTrail.permalink || ''}
                  required={isEditing}
                  startAdornment={
                    // the URL prefix is too wide for phones, which see the full URL below the field instead
                    <InputAdornment position="start" sx={{ display: { zero: 'none', sm: 'flex' } }}>
                      https://{AppStateContext.getRegion().toLowerCase()}.flowfeed.app/trail-system/
                      {tempTrailSystem?.permalink || 'parent-trail'}/
                    </InputAdornment>
                  }
                  onChange={(e) => {
                    setTempTrail((prev) => ({ ...prev, permalink: generatePermalink(e.target.value) }))
                  }}
                  onKeyDown={keyDownSubmit}
                />
                <FormHelperText>
                  <Box
                    component="span"
                    sx={{
                      display: { zero: 'block', sm: 'none' },
                      color: 'primary.main',
                      overflowWrap: 'anywhere',
                      mb: 0.5,
                    }}
                  >
                    https://{AppStateContext.getRegion().toLowerCase()}.flowfeed.app/trail-system/
                    {tempTrailSystem?.permalink || 'parent-trail'}/{tempTrail.permalink}
                  </Box>
                  This is the unique identifier for this trail used in the URL for this trail's page within the app. It
                  can only contain letters, numbers, and dashes. Invalid characters will be removed.
                  {isAdding ? ' If you leave this blank, one will be generated for you.' : ''}
                </FormHelperText>
              </FormControl>
              <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'flex-start' }}>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        id="trail-active-switch"
                        checked={tempTrail.active !== false}
                        onChange={(e) => {
                          setTempTrail((prev) => ({ ...prev, active: e.target.checked }))
                        }}
                      />
                    }
                    label={tempTrail.active !== false ? 'Active' : 'Inactive'}
                  />
                  <FormHelperText sx={{ mt: 0 }}>Inactive trails are hidden in the app.</FormHelperText>
                </FormControl>
                <DetailInput<Trail>
                  label="Sort Weight"
                  field="sortWeight"
                  type="number"
                  helperText="Lower numbers will appear first in lists (default 99)"
                  tempDetail={tempTrail}
                  setTempDetail={setTempTrail}
                  keyDownSubmit={keyDownSubmit}
                />
              </Stack>
              <DetailInput<Trail>
                label="Featured Image URL"
                field="featuredImgUrl"
                tempDetail={tempTrail}
                setTempDetail={setTempTrail}
                keyDownSubmit={keyDownSubmit}
              />
              <DetailInput<Trail>
                label="Description"
                field="description"
                tempDetail={tempTrail}
                setTempDetail={setTempTrail}
                keyDownSubmit={keyDownSubmit}
              />
            </FormSection>

            {/* --- Trail Specs -------------------------------------------------- */}
            <FormSection id={TrailSectionIds.specs}>
              <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5} sx={{ '& > *': { flex: 1, minWidth: 0 } }}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="trail-difficulty-select-label">Difficulty</InputLabel>
                  <Select
                    labelId="trail-difficulty-select-label"
                    id="trail-difficulty-select"
                    value={tempTrail.difficultyId || ''}
                    label="Difficulty"
                    onChange={(e) => {
                      setTempTrail((prev) => ({ ...prev, difficultyId: e.target.value as Difficulties }))
                    }}
                  >
                    {Object.entries(Difficulties).map((eachDifficulty) => {
                      return (
                        <MenuItem key={eachDifficulty[1]} value={eachDifficulty[1]}>
                          {startCase(camelCase(eachDifficulty[0]))}
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
                <DetailSelectWithCreateNew<Trail>
                  label="Surface Type"
                  field="surfaceType"
                  options={surfaceTypes || []}
                  tempDetail={tempTrail}
                  setTempDetail={setTempTrail}
                  keyDownSubmit={keyDownSubmit}
                />
              </Stack>
              <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5}>
                <DetailInput<Trail>
                  label="Distance (miles)"
                  field="distance"
                  type="number"
                  tempDetail={tempTrail}
                  setTempDetail={setTempTrail}
                  keyDownSubmit={keyDownSubmit}
                />
                <DetailInput<Trail>
                  label="Climb (feet)"
                  field="climb"
                  type="number"
                  tempDetail={tempTrail}
                  setTempDetail={setTempTrail}
                  keyDownSubmit={keyDownSubmit}
                />
                <DetailInput<Trail>
                  label="Descent (feet)"
                  field="descent"
                  type="number"
                  tempDetail={tempTrail}
                  setTempDetail={setTempTrail}
                  keyDownSubmit={keyDownSubmit}
                  startAdornment={<InputAdornment position="start">-</InputAdornment>}
                  displayTransformer={displayDescentValue}
                />
              </Stack>
            </FormSection>

            {/* --- Location ----------------------------------------------------- */}
            <FormSection id={TrailSectionIds.location}>
              <SectionSubheading
                title="Address"
                description="Fetching from Google Maps also fills in the trailhead GPS."
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TravelExploreIcon />}
                    onClick={() => setIsGoogleMapsDialogOpen(true)}
                    sx={{ flexShrink: 0 }}
                  >
                    Fetch from Google Maps
                  </Button>
                }
              />
              <DetailInput<Trail>
                label="Street"
                field="street"
                tempDetail={tempTrail}
                setTempDetail={setTempTrail}
                keyDownSubmit={keyDownSubmit}
              />
              <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5}>
                <DetailInput<Trail>
                  label="City"
                  field="city"
                  tempDetail={tempTrail}
                  setTempDetail={setTempTrail}
                  keyDownSubmit={keyDownSubmit}
                />
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="trail-state-select-label">State</InputLabel>
                  <Select
                    labelId="trail-state-select-label"
                    id="trail-state-select"
                    value={tempTrail.state || ''}
                    label="State"
                    onChange={(e) => {
                      setTempTrail((prev) => ({ ...prev, state: e.target.value as USStates }))
                    }}
                  >
                    {Object.entries(USStates).map((eachState) => {
                      return (
                        <MenuItem key={eachState[1]} value={eachState[1]}>
                          {startCase(camelCase(eachState[0]))}
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
              </Stack>

              <Divider />
              <SectionSubheading title="Trailhead GPS" />
              <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5}>
                <DetailInput<Trail>
                  label="GPS Latitude"
                  field="gpsLat"
                  type="number"
                  placeholder="e.g. 42.3601"
                  helperText="Up to 15 points of precision are supported."
                  tempDetail={tempTrail}
                  setTempDetail={setTempTrail}
                  keyDownSubmit={keyDownSubmit}
                />
                <DetailInput<Trail>
                  label="GPS Longitude"
                  field="gpsLong"
                  type="number"
                  placeholder="e.g. -71.0589"
                  helperText="Up to 15 points of precision are supported."
                  tempDetail={tempTrail}
                  setTempDetail={setTempTrail}
                  keyDownSubmit={keyDownSubmit}
                />
              </Stack>

              <Divider />
              {/*
               * If the trail's system has parking lots, let the admin pick one. A lot selection
               * stores the lot id on the trail and the user app sources coordinates from it.
               * "Custom location" falls back to the manual lat/long + Google Maps fetch flow
               * for one-off trails that need their own parking point.
               */}
              <SectionSubheading
                title="Parking"
                action={
                  !tempTrail.parkingLotId && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<TravelExploreIcon />}
                      onClick={() => setIsParkingDialogOpen(true)}
                      sx={{ flexShrink: 0 }}
                    >
                      Fetch from Google Maps
                    </Button>
                  )
                }
              />
              {systemParkingLots.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel id="trail-parking-lot-label">Parking Lot</InputLabel>
                  <Select
                    labelId="trail-parking-lot-label"
                    label="Parking Lot"
                    value={tempTrail.parkingLotId ?? 'custom'}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value === 'custom') {
                        setTempTrail((prev) => ({ ...prev, parkingLotId: null }))
                      } else {
                        const lotId = Number(value)
                        const selected = systemParkingLots.find((lot) => lot.id === lotId)
                        setTempTrail((prev) => ({
                          ...prev,
                          parkingLotId: lotId,
                          // Clear the custom coords when linking to a system lot, so it's obvious
                          // the lot is the source of truth for this trail's parking location.
                          parkingGpsLat: selected?.gpsLat as number | undefined,
                          parkingGpsLong: selected?.gpsLong as number | undefined,
                        }))
                      }
                    }}
                    disabled={isLoadingSystemParkingLots}
                  >
                    {systemParkingLots.map((lot) => (
                      <MenuItem key={lot.id} value={lot.id}>
                        {lot.name || `Parking Lot ${lot.id}`}
                        {typeof lot.gpsLat === 'number' && typeof lot.gpsLong === 'number'
                          ? ` (${lot.gpsLat}, ${lot.gpsLong})`
                          : ''}
                      </MenuItem>
                    ))}
                    <MenuItem value="custom">Custom location (enter coordinates below)</MenuItem>
                  </Select>
                  <FormHelperText>
                    Pick a parking lot already defined on this trail system, or choose a custom location for this trail.
                  </FormHelperText>
                </FormControl>
              )}

              {/* Manual entry only shows when no system lot is selected. */}
              {!tempTrail.parkingLotId && (
                <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5}>
                  <DetailInput<Trail>
                    label="Parking GPS Latitude"
                    field="parkingGpsLat"
                    type="number"
                    placeholder="e.g. 42.3601"
                    helperText="Up to 15 points of precision are supported."
                    tempDetail={tempTrail}
                    setTempDetail={setTempTrail}
                    keyDownSubmit={keyDownSubmit}
                  />
                  <DetailInput<Trail>
                    label="Parking GPS Longitude"
                    field="parkingGpsLong"
                    type="number"
                    placeholder="e.g. -71.0589"
                    helperText="Up to 15 points of precision are supported."
                    tempDetail={tempTrail}
                    setTempDetail={setTempTrail}
                    keyDownSubmit={keyDownSubmit}
                  />
                </Stack>
              )}
            </FormSection>

            {/* --- Map Data ----------------------------------------------------- */}
            <FormSection id={TrailSectionIds.map}>
              <SectionSubheading
                title="OpenStreetMap Relation IDs"
                description="Preferred. A relation groups every way that makes up the trail."
              />
              {(tempTrail.osmRelations ?? []).map((relation, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <DetailInput<TrailRelation>
                    label={`Relation ID ${index + 1}`}
                    field="relationId"
                    type="number"
                    placeholder="e.g. 16467819"
                    tempDetail={relation}
                    onChange={(value: string) => updateRelationId(index, 'relationId', value)}
                    keyDownSubmit={keyDownSubmit}
                  />
                  <Tooltip title="Remove">
                    <IconButton
                      aria-label={`Remove relation ID ${index + 1}`}
                      color="error"
                      onClick={() => removeRelationId(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
              <Button variant="outlined" onClick={addRelationId} fullWidth>
                + Add Relation ID
              </Button>

              <Divider>or</Divider>
              <SectionSubheading
                title="OpenStreetMap Way IDs"
                description="Use individual ways when the trail has no relation."
              />
              {tempTrail.ways?.map((way, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <DetailInput<TrailWay>
                    label={`Way ID ${index + 1}`}
                    field="wayId"
                    type="number"
                    placeholder="e.g. 1065947564"
                    tempDetail={way}
                    onChange={(value: string) => updateWayId(index, 'wayId', value)}
                    keyDownSubmit={keyDownSubmit}
                  />
                  <Tooltip title="Remove">
                    <IconButton
                      aria-label={`Remove way ID ${index + 1}`}
                      color="error"
                      onClick={() => removeWayId(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
              <Button variant="outlined" onClick={addWayId} fullWidth>
                + Add Way ID
              </Button>
            </FormSection>

            {/* --- Weather & Algorithm ------------------------------------------ */}
            <FormSection id={TrailSectionIds.weather}>
              {hasViewWeatherGroupPermissions && weatherGroupsResponse?.data && (
                <>
                  <SectionSubheading
                    title="Weather Sources"
                    description="Drag to reorder. Leave empty to use the trail system's weather groups."
                  />
                  <WeatherGroupPriorityList
                    items={tempTrail.weatherGroupsWithPriority ?? []}
                    weatherGroups={weatherGroupsResponse.data}
                    onChange={(idx, weatherGroupId) => handleWeatherGroupChange(idx, 'weatherGroupId', weatherGroupId)}
                    onRemove={handleRemoveWeatherGroup}
                    onAdd={handleAddWeatherGroup}
                    onReorder={handleReorderWeatherGroups}
                  />
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="trail-display-weather-group-select-label">Display Weather Group</InputLabel>
                    <Select
                      labelId="trail-display-weather-group-select-label"
                      id="trail-display-weather-group-select"
                      value={tempTrail.displayWeatherGroupId || ''}
                      label="Display Weather Group"
                      onChange={(e) => {
                        setTempTrail((prev) => ({
                          ...prev,
                          displayWeatherGroupId: e.target.value ? Number(e.target.value) : null,
                        }))
                      }}
                    >
                      <MenuItem value="">-Inherit from parent-</MenuItem>
                      {weatherGroupsResponse.data.map((eachWeatherGroup) => {
                        return (
                          <MenuItem key={eachWeatherGroup.id} value={eachWeatherGroup.id}>
                            {eachWeatherGroup.name}
                          </MenuItem>
                        )
                      })}
                    </Select>
                    <FormHelperText>
                      If you leave this blank, the weather group from the parent trail system will be used
                    </FormHelperText>
                  </FormControl>
                  <Divider />
                </>
              )}

              <SectionSubheading title="Algorithm Tuning" description="Leave blank to inherit from the trail system." />
              {activeTuningProfile && (
                <Alert severity="info">
                  {trail
                    ? `${activeTuningProfile.name} is the active tuning profile for ${tempTrailSystem?.name}. Saving also updates this trail in ${activeTuningProfile.name}. To change another profile instead, open it from Tuning Profiles on the trail system page.`
                    : `${activeTuningProfile.name} is the active tuning profile for ${tempTrailSystem?.name}. This trail joins ${activeTuningProfile.name} with these values, and the system's other profiles with its resistance and their system values.`}
                </Alert>
              )}
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="trail-weather-resistance-select-label">Weather Resistance</InputLabel>
                <Select
                  labelId="trail-weather-resistance-select-label"
                  id="trail-weather-resistance-select"
                  value={tempTrail.weatherResistance || 5}
                  label="Weather Resistance"
                  onChange={(e) => {
                    const value = Number(e.target.value)
                    setTempTrail((prev) => ({
                      ...prev,
                      weatherResistance: value >= 1 && value <= 20 ? (value as IntRange<1, 21>) : undefined,
                    }))
                  }}
                >
                  {Array.from({ length: 20 }, (value, index) => index + 1).map((eachResistance) => {
                    return (
                      <MenuItem key={eachResistance} value={eachResistance}>
                        {eachResistance}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
              <DetailInput<Trail>
                label="Precipitation Lookback Days"
                field="precipitationLookbackDays"
                type="number"
                helperText="Used by the Status algorithm to look back at weather records for X days. If not set, it will look at the trail system, then region, then default to 7 days. Must be a non-negative number (e.g. 0,3, etc.)."
                tempDetail={tempTrail}
                setTempDetail={setTempTrail}
                keyDownSubmit={keyDownSubmit}
              />
              <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'flex-start' }}>
                <DetailInput<Trail>
                  label="Saturation Modifier"
                  field="saturationModifier"
                  type="number"
                  helperText="e.g. 0.5 for 50% or 1.5 for 150% (default 1; 0 ignores prior-week wetness). Leave blank to inherit the system value."
                  tempDetail={tempTrail}
                  setTempDetail={setTempTrail}
                  keyDownSubmit={keyDownSubmit}
                />
                <DetailInput<Trail>
                  label="Dry-Time Modifier"
                  field="dryTimeModifier"
                  type="number"
                  helperText="A larger number makes a trail move back to low impact quicker (e.g. 0.5 for 50% or 1.5 for 150%, default 1). Leave blank to inherit from Trail System."
                  tempDetail={tempTrail}
                  setTempDetail={setTempTrail}
                  keyDownSubmit={keyDownSubmit}
                />
              </Stack>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="trail-freeze-thaw-enabled-select-label">Freeze/Thaw Enabled</InputLabel>
                <Select
                  labelId="trail-freeze-thaw-enabled-select-label"
                  id="trail-freeze-thaw-enabled-select"
                  value={
                    tempTrail.freezeThawEnabled === null || tempTrail.freezeThawEnabled === undefined
                      ? 'inherit'
                      : tempTrail.freezeThawEnabled
                      ? 'true'
                      : 'false'
                  }
                  label="Freeze/Thaw Enabled"
                  onChange={(e) => {
                    setTempTrail((prev) => ({
                      ...prev,
                      freezeThawEnabled: e.target.value === 'inherit' ? null : e.target.value === 'true' ? true : false,
                    }))
                  }}
                >
                  <MenuItem value="inherit">Inherit (from Trail System or Region)</MenuItem>
                  <MenuItem value="true">Enabled</MenuItem>
                  <MenuItem value="false">Disabled</MenuItem>
                </Select>
                <FormHelperText>
                  Enable or disable Freeze/Thaw algorithm for this trail. If set to Inherit, will use Trail System
                  setting, then Region setting.
                </FormHelperText>
              </FormControl>

              {hasViewConditionalStatusGroupPermissions && conditionalStatusGroupsResponse?.data && (
                <>
                  <Divider />
                  <SectionSubheading title="Conditional Statuses" />
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="trail-conditional-status-groups-select-label">Conditional Status Groups</InputLabel>
                    <Select
                      labelId="trail-conditional-status-groups-select-label"
                      id="trail-conditional-status-groups-select"
                      value={tempTrail.conditionalStatusGroupIds || []}
                      label="Conditional Status Groups"
                      multiple
                      onChange={(e) => {
                        setTempTrail((prev) => ({
                          ...prev,
                          conditionalStatusGroupIds:
                            typeof e.target.value === 'string'
                              ? e.target.value.split(',').map((val) => Number(val))
                              : e.target.value,
                        }))
                      }}
                    >
                      {conditionalStatusGroupsResponse.data.map((eachConditionalStatusGroup) => {
                        return (
                          <MenuItem key={eachConditionalStatusGroup.id} value={eachConditionalStatusGroup.id}>
                            {eachConditionalStatusGroup.name}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                </>
              )}
            </FormSection>

            {/* --- Integrations section (Trailforks-Pusher-Spec.md) ------------- */}
            <FormSection id={TrailSectionIds.integrations}>
              <SectionSubheading title="Trailforks" />
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="trail-trailforks-sync-enabled-select-label">Trailforks Status Sync</InputLabel>
                <Select
                  labelId="trail-trailforks-sync-enabled-select-label"
                  id="trail-trailforks-sync-enabled-select"
                  value={
                    tempTrail.trailforksSyncEnabled === null || tempTrail.trailforksSyncEnabled === undefined
                      ? 'inherit'
                      : tempTrail.trailforksSyncEnabled
                      ? 'true'
                      : 'false'
                  }
                  label="Trailforks Status Sync"
                  onChange={(e) => {
                    setTempTrail((prev) => ({
                      ...prev,
                      trailforksSyncEnabled:
                        e.target.value === 'inherit' ? null : e.target.value === 'true' ? true : false,
                    }))
                  }}
                >
                  <MenuItem value="inherit">
                    Inherit (from Trail System: {tempTrailSystem?.trailforksSyncEnabled ? 'Enabled' : 'Disabled'})
                  </MenuItem>
                  <MenuItem value="true">Enabled</MenuItem>
                  <MenuItem value="false">Disabled</MenuItem>
                </Select>
                <FormHelperText>
                  Push this trail's status to Trailforks hourly. Requires at least one Trailforks Trail ID below.
                  Inherit uses the Trail System setting.
                </FormHelperText>
              </FormControl>
              {(tempTrail.trailforksIds ?? []).map((link, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>{`Trailforks Trail ID ${index + 1}`}</InputLabel>
                    <OutlinedInput
                      label={`Trailforks Trail ID ${index + 1}`}
                      placeholder="e.g. 156847"
                      value={link.trailforksTrailId || ''}
                      onChange={(e) => updateTrailforksId(index, e.target.value)}
                      onKeyDown={keyDownSubmit}
                    />
                  </FormControl>
                  <Tooltip title="Remove">
                    <IconButton
                      aria-label={`Remove Trailforks trail ID ${index + 1}`}
                      color="error"
                      onClick={() => removeTrailforksId(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
              <Box>
                <Button variant="outlined" onClick={addTrailforksId} fullWidth>
                  + Add Trailforks Trail ID
                </Button>
                <FormHelperText sx={{ mx: 1.75 }}>
                  The numeric trail id from Trailforks (last segment of the trail URL). Add more rows to fan out one
                  Flowfeed trail to several Trailforks trails.
                </FormHelperText>
              </Box>
            </FormSection>
          </SectionedForm>
          <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              Save
            </Button>
          </Stack>
        </>
      )}
      {/* Google Maps Dialogs */}
      <GoogleMapsFetchDialog
        open={isGoogleMapsDialogOpen}
        onClose={() => setIsGoogleMapsDialogOpen(false)}
        dialogTitle="Fetch Address Details from Google Maps URL"
        onFetch={(data) => {
          setTempTrail((prev) => ({
            ...prev,
            street: data.street !== undefined ? data.street : prev.street,
            city: data.city !== undefined ? data.city : prev.city,
            state: data.state !== undefined ? (data.state as USStates) : prev.state,
            gpsLat: data.latitude !== undefined ? Number(data.latitude) : prev.gpsLat,
            gpsLong: data.longitude !== undefined ? Number(data.longitude) : prev.gpsLong,
          }))
        }}
      />
      <GoogleMapsFetchDialog
        open={isParkingDialogOpen}
        onClose={() => setIsParkingDialogOpen(false)}
        dialogTitle="Fetch Parking Details from Google Maps URL"
        onFetch={(data) => {
          setTempTrail((prev) => ({
            ...prev,
            parkingGpsLat: data.latitude !== undefined ? Number(data.latitude) : prev.parkingGpsLat,
            parkingGpsLong: data.longitude !== undefined ? Number(data.longitude) : prev.parkingGpsLong,
          }))
        }}
      />
    </>
  )
}
export default TrailForm
